import React, { useCallback, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Box } from "@mantine/core";
import { useMessaging } from "../../context/MessagingContext";
import { useSalon } from "../../context/SalonContext";
import { useResponsive } from "../../context/ResponsiveContext";
import { ConversationList } from "./ConversationList";
import { ConversationView } from "./ConversationView";
import type { MessageMediaRef } from "../../types/messaging";
import styles from "./ChatPage.module.css";

/**
 * Two-way SMS/MMS Chat — a route-driven master/detail:
 *   /admin/chat                     → conversation list
 *   /admin/chat/:conversationId     → thread (shareable deep link)
 *
 * Desktop shows both panes side by side; on mobile (≤768px) it drills down —
 * the list until a conversation is opened, then the thread full-width with a
 * back bar. Gated upstream by the flag + `messaging` permission + a Twilio
 * location (see MessagingRoutes in App.js).
 */
const ChatPage: React.FC = () => {
  const { conversationId } = useParams<{ conversationId?: string }>();
  const navigate = useNavigate();
  const { isMobile } = useResponsive();
  const { businessInfo } = useSalon() as {
    businessInfo: { timezone?: string } | null;
  };
  // Business tz — used for the scheduling overlay (compose/show future sends in
  // the salon's tz). Thread message times render in the viewer's local tz.
  const timezone = businessInfo?.timezone || "America/New_York";

  const {
    conversations,
    conversationsLoading,
    fetchConversations,
    activeConversation,
    messages,
    messagesLoading,
    loadingEarlier,
    hasMoreMessages,
    fetchThread,
    clearThread,
    sendMessage,
    setReadState,
  } = useMessaging();

  // Load the inbox on mount / salon switch.
  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  // Open (or clear) the thread as the route param changes.
  useEffect(() => {
    if (!conversationId) {
      clearThread();
      return;
    }
    fetchThread(conversationId);
  }, [conversationId, fetchThread, clearThread]);

  // Leaving the Chat page entirely must clear the active thread so the app-wide
  // realtime handler (MessagingContext) stops treating the last-opened thread as
  // still "active" and auto-reading inbound texts to it. Unmount-only cleanup —
  // clearThread is a stable useCallback, so this never runs on a re-render.
  useEffect(() => () => clearThread(), [clearThread]);

  // Auto-mark-read only while ACTIVELY viewing the thread: the Chat page is open
  // on this conversation AND this browser tab is in the foreground. Gating on
  // visibility (with the unmount clear above) stops inbound texts from being
  // silently read while the user is on another page or another browser tab.
  const markCurrentThreadRead = useCallback(() => {
    if (!conversationId) return;
    if (document.visibilityState !== "visible") return;
    const conv = conversations.find(
      (c) => c.conversation_id === conversationId,
    );
    if (conv?.is_unread) setReadState(conversationId, false);
  }, [conversationId, conversations, setReadState]);

  // Fire once on a genuine open/re-open — never when the `conversations` array
  // merely changes while the same thread stays open. Without this guard, "mark
  // as unread" (which mutates `conversations`) would re-fire and immediately
  // re-read the still-open thread. `autoReadForId` records the conversation
  // we've already acted on for this open; we wait for the inbox to load (conv
  // found) before acting so a not-yet-loaded thread still gets read.
  const autoReadForId = useRef<string | null>(null);
  useEffect(() => {
    if (!conversationId) {
      autoReadForId.current = null;
      return;
    }
    if (autoReadForId.current === conversationId) return;
    const conv = conversations.find(
      (c) => c.conversation_id === conversationId,
    );
    if (!conv) return;
    autoReadForId.current = conversationId;
    markCurrentThreadRead();
  }, [conversationId, conversations, markCurrentThreadRead]);

  // When this tab regains focus while a thread is open, mark it read — an inbound
  // that arrived while the tab was backgrounded intentionally stayed unread, and
  // returning to it counts as "actively viewing".
  useEffect(() => {
    document.addEventListener("visibilitychange", markCurrentThreadRead);
    return () =>
      document.removeEventListener("visibilitychange", markCurrentThreadRead);
  }, [markCurrentThreadRead]);

  const handleSelect = (id: string) => navigate(`/admin/chat/${id}`);
  const handleBack = () => navigate("/admin/chat");
  const handleSend = async (body: string, media?: MessageMediaRef[]) => {
    if (conversationId) await sendMessage(conversationId, body, media);
  };
  const handleLoadEarlier = () => {
    if (conversationId && messages.length > 0) {
      fetchThread(conversationId, { before: messages[0].created_dt });
    }
  };

  const list = (
    <ConversationList
      conversations={conversations}
      loading={conversationsLoading}
      activeConversationId={conversationId}
      onSelect={handleSelect}
    />
  );

  // Show the target contact in the header the instant a switch happens: the
  // fetched thread's `activeConversation` lags the route while it loads, so fall
  // back to the inbox row (same `Conversation` shape, already loaded) until it
  // catches up.
  const headerConversation =
    activeConversation?.conversation_id === conversationId
      ? activeConversation
      : (conversations.find((c) => c.conversation_id === conversationId) ??
        activeConversation);

  const view = (
    <ConversationView
      conversation={headerConversation}
      messages={messages}
      loading={messagesLoading}
      loadingEarlier={loadingEarlier}
      hasMore={hasMoreMessages}
      timezone={timezone}
      onSend={handleSend}
      onLoadEarlier={handleLoadEarlier}
      showBackBar={isMobile}
      onBack={handleBack}
    />
  );

  // Mobile drill-down: one pane at a time, keyed off the route param.
  if (isMobile) {
    return <Box className={styles.root}>{conversationId ? view : list}</Box>;
  }

  return (
    <Box className={styles.root}>
      <Box className={styles.listPane}>{list}</Box>
      <Box className={styles.viewPane}>{view}</Box>
    </Box>
  );
};

export default ChatPage;
